import type { WidgetConfig } from '@springboard/shared';

/** Console API client. Talks to services/api (planning.md FR-P-02). Token is only needed when the API sets SB_ADMIN_TOKEN. */
/** Same origin in production builds (the API is served from the same host); localhost in dev. */
export const API_BASE = (import.meta.env.VITE_API_BASE ?? (import.meta.env.DEV ? 'http://localhost:8787' : '')).replace(/\/$/, '');
const TOKEN_KEY = 'sb_admin_token';
const SESSION_KEY = 'sb_session';

export interface Session {
  idToken: string;
  refreshToken?: string;
  expiresAt: number;
  email: string;
  accountId?: string;
}

/** Operator token (all sites) — kept for the owner and local development. */
export const auth = {
  get token(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  },
  set token(v: string | null) {
    if (v) localStorage.setItem(TOKEN_KEY, v);
    else localStorage.removeItem(TOKEN_KEY);
  },
  get session(): Session | null {
    try {
      const raw = localStorage.getItem(SESSION_KEY);
      return raw ? (JSON.parse(raw) as Session) : null;
    } catch {
      return null;
    }
  },
  set session(v: Session | null) {
    if (v) localStorage.setItem(SESSION_KEY, JSON.stringify(v));
    else localStorage.removeItem(SESSION_KEY);
  },
  get signedIn(): boolean {
    return !!(this.token || this.session);
  },
  signOut(): void {
    this.token = null;
    this.session = null;
  },
};

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly issues?: unknown,
  ) {
    super(message);
  }
}

let refreshing: Promise<boolean> | null = null;
async function refreshSession(): Promise<boolean> {
  const s = auth.session;
  if (!s?.refreshToken) return false;
  refreshing ??= (async () => {
    try {
      const res = await fetch(`${API_BASE}/v1/auth/refresh`, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ refreshToken: s.refreshToken }) });
      if (!res.ok) return false;
      const t = (await res.json()) as AuthTokens;
      auth.session = { idToken: t.idToken, refreshToken: t.refreshToken ?? s.refreshToken, expiresAt: Date.now() + t.expiresIn * 1000, email: t.email || s.email, accountId: t.accountId ?? s.accountId };
      return true;
    } catch {
      return false;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

function bearer(): string | null {
  if (auth.token) return auth.token;
  return auth.session?.idToken ?? null;
}

async function request<T>(method: string, path: string, body?: unknown, retry = true): Promise<T> {
  const token = bearer();
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: { ...(body !== undefined ? { 'content-type': 'application/json' } : {}), ...(token ? { authorization: `Bearer ${token}` } : {}) },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (res.status === 401 && path.startsWith('/v1/admin')) {
    if (retry && auth.session && (await refreshSession())) return request<T>(method, path, body, false);
    if (auth.session) auth.session = null;
    window.dispatchEvent(new Event('sb:unauthorised'));
  }
  if (res.status === 204) return undefined as T;
  const data = (await res.json().catch(() => ({}))) as { error?: string; issues?: unknown };
  if (!res.ok) throw new ApiError(res.status, data.error ?? `Request failed (${res.status})`, data.issues);
  return data as T;
}

export interface AuthTokens {
  idToken: string;
  accessToken: string;
  refreshToken?: string;
  expiresIn: number;
  email: string;
  accountId?: string;
}

// ---------- types mirrored from services/scan/src/repository/types.ts ----------
export interface Site {
  search?: { enabled: boolean; placeholder?: string };
  rescan?: { daily: boolean; hourUtc: number; lastAutoScanAt?: string; lastAutoScanId?: string };
  guidance?: { notes: string; updatedAt: string };
  install?: { lastSeenAt: string; origin?: string; loads: number };
  siteId: string;
  accountId: string;
  domain: string;
  primaryUrl: string;
  siteKey: string;
  allowedOrigins: string[];
  status: 'new' | 'scanning' | 'ready' | 'failed';
  brandName?: string;
  siteSummary?: string;
  publishedConfigVersion?: number;
  draftConfigVersion?: number;
  lastScanId?: string;
  theme?: Partial<WidgetConfig['theme']>;
  trigger?: Partial<WidgetConfig['trigger']>;
  createdAt: string;
  updatedAt: string;
}
export interface Scan {
  scanId: string;
  siteId: string;
  mode: 'full' | 'category';
  categoryId?: string;
  status: 'pending' | 'crawling' | 'analysing' | 'indexing' | 'ready' | 'failed';
  analyser: string;
  startedAt: string;
  finishedAt?: string;
  pagesPlanned?: number;
  pagesCrawled?: number;
  pagesClassified?: number;
  message?: string;
  error?: string;
  costUsd?: number; trigger?: 'manual' | 'scheduled';
  draftVersion?: number;
}
export interface Category { siteId: string; categoryId: string; label: string; description: string; intentTags: string[]; order: number; visible: boolean; source: 'ai' | 'manual'; confidence?: number; updatedAt: string }
export interface Item { siteId: string; itemId: string; categoryId: string; title: string; description: string; url: string; ctaLabel: string; itemType: 'page' | 'cta' | 'lead_form' | 'phone' | 'email' | 'external'; thumbnailUrl?: string; order: number; visible: boolean; source: 'ai' | 'manual'; confidence?: number; updatedAt: string }
export interface Question { siteId: string; questionId: string; text: string; order: number; enabled: boolean; options: { optionId: string; label: string; categoryIds: string[] }[]; updatedAt: string }
export interface Goal { siteId: string; goalId: string; type: 'form_submit' | 'url_match' | 'cta_click' | 'custom'; label: string; match: { pathPattern?: string; urlPattern?: string; selector?: string }; isPrimary: boolean; source: 'ai' | 'manual' }
export interface VersionMeta { siteId: string; version: number; status: 'draft' | 'published' | 'archived'; changeSummary: string; createdAt: string; publishedAt?: string }
export interface SiteBundle { site: Site; categories: Category[]; items: Item[]; questions: Question[]; goals: Goal[]; versions: VersionMeta[]; scans: Scan[]; snippet: string; consoleUrl: string }
export interface PageRow { url: string; title: string; statusCode: number; depth: number; pageType?: string; cardTitle?: string; ctas: number; leads: number; thumbnail: string | null; error: string | null; stage: 'crawled' | 'classified' | 'failed'; current: boolean }
export interface ArmCounters { visitors: number; pageViews: number; widgetShown: number; answers: number; cardClicks: number; dismissed: number; conversionEvents: number; convertedVisitors: Record<string, number> }
export interface Experiment { siteId: string; experimentId: string; status: 'running' | 'ended' | 'productionised'; split: number; controlVersion?: number; variationVersionAtStart: number; primaryGoalId?: string; startedAt: string; plannedEndAt: string; extensions: number; endedAt?: string; endedReason?: string; note?: string }
export interface ExperimentStats {
  control: { visitors: number; conversions: number; rate: number };
  variation: { visitors: number; conversions: number; rate: number };
  probVariationBeats: number;
  lift: { point: number; low: number; high: number } | null;
  expectedLoss: { control: number; variation: number };
  zTestPValue: number | null;
  decision: 'winner_variation' | 'winner_control' | 'inconclusive' | 'insufficient';
  reasons: string[];
  sampleSizeTarget: { relativeLift: number; perArm: number } | null;
  projectedDaysToTarget: number | null;
}
export interface ExperimentReport { experiment: Experiment | null; arms: { control: ArmCounters; variation: ArmCounters }; daily: { date: string; control: ArmCounters; variation: ArmCounters }[]; primaryGoalId?: string; stats: ExperimentStats | null; daysRunning: number; daysLeft: number | null; expired: boolean; publishedVersions: number[] }
export interface AccountSummary {
  account: { accountId: string; email: string; status: 'trial' | 'active' | 'past_due' | 'cancelled' | 'disabled'; createdAt: string };
  plan: 'free' | 'paid';
  planLabel: string;
  limits: { sites: number | null; scansTotal: number | null; maxPages: number; dailyRescan: boolean };
  usage: { sites: number; scansUsed: number };
  trial: { phase: 'not_started' | 'running' | 'grace' | 'expired' | 'paid' | 'cancelled'; trialStartsAt?: string; trialEndsAt?: string; graceEndsAt?: string; daysLeft: number | null };
  billing: { provider: string; customerId?: string; subscriptionId?: string; checkoutId?: string; subscriptionStatus?: string; currentPeriodEndsAt?: string; cancelledAt?: string; updatedAt: string } | null;
  provider: 'mock' | 'airwallex' | null;
  priceUsd: number;
}
export interface EventSummary {
  days: number;
  byVariant: Record<'control' | 'variation', { visitors: number; pageViews: number; widgetShown: number; answers: number; cardClicks: number; conversions: number; dismissed: number }>;
  topItems: { itemId: string; clicks: number }[];
  topOptions: { optionId: string; count: number }[];
  lastEventAt?: string;
}

// ---------- calls ----------
export const api = {
  health: () => request<{ ok: boolean; analyser: string }>('GET', '/health'),
  login: async (email: string, password: string) => {
    const t = await request<AuthTokens>('POST', '/v1/auth/login', { email, password });
    auth.session = { idToken: t.idToken, refreshToken: t.refreshToken, expiresAt: Date.now() + t.expiresIn * 1000, email: t.email, accountId: t.accountId };
    return t;
  },
  setPassword: (token: string, password: string) => request<{ ok: boolean; email: string }>('POST', '/v1/auth/set-password', { token, password }),
  forgot: (email: string) => request<{ ok: boolean }>('POST', '/v1/auth/forgot', { email }),
  forgotConfirm: (email: string, code: string, password: string) => request<{ ok: boolean }>('POST', '/v1/auth/forgot/confirm', { email, code, password }),
  me: () => request<{ kind: 'operator' } | { kind: 'user'; accountId: string; email: string }>('GET', '/v1/auth/me'),
  sites: () => request<{ sites: Site[] }>('GET', '/v1/admin/sites'),
  site: (id: string) => request<SiteBundle>('GET', `/v1/admin/sites/${id}`),
  patchSite: (id: string, patch: Partial<Pick<Site, 'brandName' | 'allowedOrigins' | 'theme' | 'trigger' | 'search'>> & { rescan?: { daily: boolean; hourUtc: number }; guidance?: { notes: string } }) => request<{ site: Site }>('PATCH', `/v1/admin/sites/${id}`, patch),
  pages: (id: string) => request<{ pages: PageRow[]; running: boolean; activeScanId?: string }>('GET', `/v1/admin/sites/${id}/pages`),
  createScan: (url: string, email: string, maxPages?: number) => request<{ scanId: string; siteId: string }>('POST', '/v1/scans', { url, email, maxPages }),
  scan: (scanId: string) => request<{ scanId: string; status: Scan['status']; message?: string; pagesCrawled?: number; pagesPlanned?: number }>('GET', `/v1/scans/${scanId}`),
  scans: (id: string) => request<{ scans: Scan[] }>('GET', `/v1/admin/sites/${id}/scans`),
  rescan: (id: string, categoryId: string) => request<{ scan: Scan }>('POST', `/v1/admin/sites/${id}/rescan`, { categoryId }),
  rescanAll: (id: string, maxPages?: number) => request<{ scan: Scan }>('POST', `/v1/admin/sites/${id}/rescan-all`, maxPages ? { maxPages } : {}),
  createCategory: (id: string, body: { label: string; description?: string }) => request<{ category: Category }>('POST', `/v1/admin/sites/${id}/categories`, body),
  updateCategory: (id: string, categoryId: string, patch: Partial<Category>) => request<{ category: Category }>('PUT', `/v1/admin/sites/${id}/categories/${categoryId}`, patch),
  deleteCategory: (id: string, categoryId: string) => request<void>('DELETE', `/v1/admin/sites/${id}/categories/${categoryId}`),
  createItem: (id: string, body: Partial<Item> & { categoryId: string; title: string; url: string }) => request<{ item: Item }>('POST', `/v1/admin/sites/${id}/items`, body),
  updateItem: (id: string, itemId: string, patch: Partial<Item> & { thumbnailUrl?: string | null }) => request<{ item: Item }>('PUT', `/v1/admin/sites/${id}/items/${itemId}`, patch),
  deleteItem: (id: string, itemId: string) => request<void>('DELETE', `/v1/admin/sites/${id}/items/${itemId}`),
  createQuestion: (id: string, body: { text: string; options: { label: string; categoryIds: string[] }[] }) => request<{ question: Question }>('POST', `/v1/admin/sites/${id}/questions`, body),
  updateQuestion: (id: string, questionId: string, patch: Partial<Question>) => request<{ question: Question }>('PUT', `/v1/admin/sites/${id}/questions/${questionId}`, patch),
  deleteQuestion: (id: string, questionId: string) => request<void>('DELETE', `/v1/admin/sites/${id}/questions/${questionId}`),
  createGoal: (id: string, body: Omit<Goal, 'siteId' | 'goalId' | 'source'>) => request<{ goal: Goal }>('POST', `/v1/admin/sites/${id}/goals`, body),
  updateGoal: (id: string, goalId: string, patch: Partial<Goal>) => request<{ goal: Goal }>('PUT', `/v1/admin/sites/${id}/goals/${goalId}`, patch),
  deleteGoal: (id: string, goalId: string) => request<void>('DELETE', `/v1/admin/sites/${id}/goals/${goalId}`),
  publish: (id: string, changeSummary: string) => request<{ version: VersionMeta }>('POST', `/v1/admin/sites/${id}/publish`, { changeSummary }),
  publishVersion: (id: string, version: number) => request<{ version: VersionMeta }>('POST', `/v1/admin/sites/${id}/versions/${version}/publish`, {}),
  version: (id: string, version: number) => request<VersionMeta & { config: WidgetConfig }>('GET', `/v1/admin/sites/${id}/versions/${version}`),
  eventsSummary: (id: string) => request<EventSummary>('GET', `/v1/admin/sites/${id}/events/summary`),
  account: (accountId?: string) => request<AccountSummary>('GET', `/v1/admin/account${accountId ? `?accountId=${encodeURIComponent(accountId)}` : ''}`),
  checkout: (accountId?: string) => request<{ url: string; checkoutId: string; provider: string }>('POST', `/v1/admin/account/checkout${accountId ? `?accountId=${encodeURIComponent(accountId)}` : ''}`, {}),
  syncCheckout: (checkoutId: string, opts: { accountId?: string; mock?: boolean }) =>
    request<AccountSummary & { checkout: { status: string } }>('POST', `/v1/admin/account/checkout/${encodeURIComponent(checkoutId)}/sync?${opts.accountId ? `accountId=${encodeURIComponent(opts.accountId)}&` : ''}${opts.mock ? 'mock=1' : ''}`, {}),
  cancelSubscription: (accountId?: string) => request<AccountSummary>('POST', `/v1/admin/account/cancel${accountId ? `?accountId=${encodeURIComponent(accountId)}` : ''}`, {}),
  experiment: (id: string) => request<ExperimentReport>('GET', `/v1/admin/sites/${id}/experiment`),
  startExperiment: (id: string, body: { controlVersion?: number | null; split?: number; note?: string }) => request<{ experiment: Experiment }>('POST', `/v1/admin/sites/${id}/experiment/start`, body),
  extendExperiment: (id: string, days: number) => request<{ experiment: Experiment }>('POST', `/v1/admin/sites/${id}/experiment/extend`, { days }),
  endExperiment: (id: string) => request<{ experiment: Experiment }>('POST', `/v1/admin/sites/${id}/experiment/end`, {}),
  productionise: (id: string) => request<{ experiment: Experiment }>('POST', `/v1/admin/sites/${id}/experiment/productionise`, {}),
  // Preview links are copied and opened outside the console, so they are always absolute (same-origin builds have an empty API_BASE).
  previewConfigUrl: (id: string) => {
    const t = auth.token ?? auth.session?.idToken;
    return `${API_BASE || window.location.origin}/v1/admin/sites/${id}/preview-config${t ? `?token=${encodeURIComponent(t)}` : ''}`;
  },
  demoUrl: (params: Record<string, string>) => `${API_BASE || window.location.origin}/widget/demo/index.html?${new URLSearchParams(params).toString()}`,
};
